/**
 * Base Navigation Class
 * Common navigation functionality that can be extended by specific navigation modules
 */

import { debounce, safeAddEventListener, getViewport, devLog } from './utils.js';

export class BaseNavigation {
    constructor(options = {}) {
        this.options = {
            selector: '.main-nav',
            mobileBreakpoint: 1024,
            ...options
        };
        
        this.nav = null;
        this.isMobile = false;
        this.init();
    }

    init() {
        this.findElements();
        this.bindEvents();
        this.checkMobileState();
    }

    findElements() {
        this.nav = document.querySelector(this.options.selector);
        if (!this.nav) {
            devLog(`BaseNavigation: Navigation element not found: ${this.options.selector}`);
        }
    }

    bindEvents() {
        // Window resize with debouncing
        window.addEventListener('resize', debounce(() => {
            this.checkMobileState();
            this.onResize();
        }, 250));

        // Document click for closing dropdowns
        document.addEventListener('click', this.handleDocumentClick.bind(this));
    }

    checkMobileState() {
        const viewport = getViewport();
        const wasMobile = this.isMobile;
        this.isMobile = viewport.width < this.options.mobileBreakpoint;
        
        if (wasMobile !== this.isMobile) {
            this.onMobileStateChange(this.isMobile);
        }
    }

    handleDocumentClick(event) {
        // Override in child classes for specific behavior
    }

    onResize() {
        // Override in child classes for specific resize behavior
    }

    onMobileStateChange(isMobile) {
        // Override in child classes for mobile state changes
        devLog(`BaseNavigation: Mobile state changed to: ${isMobile}`);
    }

    // Common navigation utility methods
    closeAllDropdowns() {
        const dropdowns = this.nav?.querySelectorAll('.sub-menu') || [];
        dropdowns.forEach(dropdown => {
            dropdown.style.display = 'none';
        });
    }

    toggleSubmenu(toggle, submenu) {
        const isOpen = toggle.classList.contains('is-open');
        
        if (isOpen) {
            this.closeSubmenu(toggle, submenu);
        } else {
            this.openSubmenu(toggle, submenu);
        }
    }

    openSubmenu(toggle, submenu) {
        toggle.classList.add('is-open');
        submenu.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
    }

    closeSubmenu(toggle, submenu) {
        toggle.classList.remove('is-open');
        submenu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
    }

    // Safe event binding with existence check
    safeBindEvent(element, event, handler, options = {}) {
        safeAddEventListener(element, event, handler, options);
    }

    // Get navigation container for positioning
    getNavContainer() {
        return this.nav?.closest('.main-navigation .inside-navigation') || 
               this.nav?.closest('.main-navigation') ||
               this.nav?.parentElement;
    }
} 